import React from 'react';
import { TaskStatus } from '@/types/tasks.ts';
import { isTaskQueued } from '@/utils/taskUtils.ts';
import { logTask } from '@/context/house/tasks/logging.ts';
import { CONCURRENT_TASK_TYPES, TOTAL_TASK_STEPS, UITask } from '@/context/house/tasks/types.ts';
import type { Runtime } from '@/context/house/tasks/runtime';
import type { UiTaskAction } from '@/context/house/tasks/state/uiTaskReducer';
import { actions as uiActions } from '@/context/house/tasks/state/uiTaskReducer';

/**
 * Produces the cancelTask callback used by HouseTaskManagerContext.
 *
 * Why: Keep the provider lean and declarative by extracting imperative logic
 * into focused action factories. This makes the code easier to follow and paves
 * the way to move other actions (retryTask, scheduleTaskBatch) out of the context body.
 */
import { isTaskDelete, makeDeleteKeyFromMeta } from '@/context/house/tasks/helpers';
import { getUITaskStatus } from '@/context/house/tasks/uiTask.ts';

export function createCancelTask(deps: {
	globalTasksRef: React.MutableRefObject<Map<string, UITask>>;
	uiTasksRef: React.MutableRefObject<UITask[]>;
	runtime: Runtime;
	dispatch: React.Dispatch<UiTaskAction>;
	onTaskCancelled: (taskId: string, batchId: string) => Promise<void>;
}) {
	const { globalTasksRef, uiTasksRef, runtime, dispatch, onTaskCancelled } = deps;

	return async (taskId: string): Promise<void> => {
		// Try to find task in global map first (active), then in UI list (may be failed)
		const t = globalTasksRef.current.get(taskId) ?? uiTasksRef.current.find((u) => u.id === taskId);
		if (!t) return;

		const isQueued = isTaskQueued(t.execution.apiTask);
		const isDispatched = runtime.marks.dispatched.has(taskId);
		// Whether a request is actually on the wire. `dispatched` only says a slot is claimed, and a
		// task waiting out a retry backoff keeps that claim on purpose — releasing it early would let
		// the scheduler pick the task up again before the backoff had elapsed.
		const isExecuting = runtime.marks.executing.has(taskId);

		const settleCancelledTask = async (message: string) => {
			logTask(taskId, TOTAL_TASK_STEPS, 'info', message);
			uiTasksRef.current = uiTasksRef.current.map((task) =>
				task.id === taskId
					? {
							...task,
							execution: {
								...task.execution,
								apiTask: { ...task.execution.apiTask, status: 'cancelled' },
							},
						}
					: task,
			);
			dispatch(uiActions.updateStatus(taskId, 'cancelled', undefined));
			await onTaskCancelled(taskId, t.batchId);
		};

		// Queued and nothing executing: no request exists to abort, so this always cancels — including
		// a task counting down a retry, which keeps its dispatch mark for the whole wait. That mark is
		// why the test is `isExecuting`: a task holding it is not necessarily doing anything, and the
		// refusal below would read a whole backoff as "already in flight".
		if (isQueued && !isExecuting) {
			await settleCancelledTask(`Cancelling queued task.`);
			return;
		}

		const isActiveUpload =
			t.taskType === CONCURRENT_TASK_TYPES.UPLOAD_RECORD &&
			(isDispatched || getUITaskStatus(t) === TaskStatus.InProgress);
		if (isActiveUpload && runtime.abortTask(taskId)) {
			await settleCancelledTask(`Cancelling active upload request.`);
			return;
		}

		// Once the upload request is accepted, the server owns pipeline processing.
		// Keep that work visible so reconciliation can settle its batch.
		if (isDispatched || getUITaskStatus(t) === TaskStatus.Pending || getUITaskStatus(t) === TaskStatus.InProgress) {
			logTask(taskId, TOTAL_TASK_STEPS, 'info', `Task is already in flight and cannot be cancelled.`);
			return;
		}

		// Terminal entries can be removed from queue history.
		// Clean up tracking and remove from UI list
		runtime.releaseDispatched(taskId);
		globalTasksRef.current.delete(taskId);
		if (isTaskDelete(t)) {
			const key = makeDeleteKeyFromMeta(t.execution.apiTask.metadata as Record<string, unknown>);
			if (key) runtime.activeDeleteKeys.delete(key);
		}
		const filtered = uiTasksRef.current.filter((u) => u.id !== taskId);
		dispatch(uiActions.replaceAll(filtered));
	};
}
